import React, {useState, useContext, useMemo, useCallback, useRef} from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import {useMediaQuery} from "react-responsive";
import NoSsr from "@material-ui/core/NoSsr";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "../../../common/Icons/CloseIcon";
import {SubMenus} from "../SubMenus";
import {SidebarPane} from "../SidebarPane";
import {Context} from "../../index";
import {useOnClickOutside} from "../../../../utils/use-on-click-outside";

import styles from "./menus.module.scss";
import chevronIcon from "../../assets/chevron.svg";

function MenusContainer({children, isDesktop, isOpen, searchMode, close}) {
  const containerRef = useRef(null);
  useOnClickOutside(!isDesktop && isOpen && containerRef, close);
  return (
    <div
      className={classnames(styles.menusContainer, {[styles.active]: isOpen})}
    >
      <div
        className={classnames(styles.menus, {
          [styles.active]: isOpen,
          [styles.searchMode]: searchMode
        })}
        ref={containerRef}
      >
        {children}
      </div>
    </div>
  );
}

MenusContainer.propTypes = {
  children: PropTypes.array,
  isDesktop: PropTypes.bool,
  isOpen: PropTypes.bool,
  searchMode: PropTypes.bool,
  close: PropTypes.func
};

export function Menus({searchMode, isOpen, close}) {
  const {menu} = useContext(Context);
  const isDesktop = useMediaQuery({minWidth: 940});
  const [focusIndex, setFocusIndex] = useState(-1);
  const [focusTimeout, setFocusTimeout] = useState(null);

  const hoverActivationHandler = useCallback(
    (index) => {
      if (isDesktop) {
        if (focusTimeout) {
          clearTimeout(focusTimeout);
          setFocusTimeout(null);
        }
        setFocusIndex(index);
      }
    },
    [isDesktop, focusTimeout]
  );

  const hoverDeactivationHandler = useCallback(
    (isImmediate) => {
      if (isDesktop) {
        if (isImmediate) {
          return setFocusIndex(-1);
        }
        setFocusTimeout(setTimeout(() => setFocusIndex(-1), 750));
      }
    },
    [isDesktop]
  );

  const onClickHandler = useCallback(
    (index) => {
      setFocusIndex(index === focusIndex ? -1 : index);
    },
    [focusIndex]
  );

  const menusElements = useMemo(
    () =>
      menu.map((menusItem, index) => {
        return (
          <li key={menusItem.title}>
            <div className={styles.menusItemRow}>
              <a
                className={classnames({[styles.active]: menusItem.isActive})}
                href={menusItem.path}
                onMouseEnter={() => hoverActivationHandler(index)}
                onMouseLeave={() => hoverDeactivationHandler()}
                onFocus={() => hoverActivationHandler(index)}
              >
                {menusItem.title}
              </a>
              {menusItem.children && (
                <button
                  className={classnames(styles.chevron, {
                    [styles.active]: focusIndex === index
                  })}
                  onClick={() => onClickHandler(index)}
                >
                  <img src={chevronIcon} alt="chevron icon" />
                </button>
              )}
            </div>
            {menusItem.children && (
              <SubMenus
                subMenus={menusItem.children}
                isFocused={focusIndex === index}
                hide={() => hoverDeactivationHandler(true)}
                keepFocused={() => hoverActivationHandler(index)}
              />
            )}
          </li>
        );
      }),
    [
      menu,
      focusIndex,
      hoverActivationHandler,
      hoverDeactivationHandler,
      onClickHandler
    ]
  );

  return (
    <MenusContainer
      isDesktop={isDesktop}
      isOpen={isOpen}
      searchMode={searchMode}
      close={close}
    >
      <NoSsr>
        {!isDesktop && (
          <IconButton
            onClick={close}
            className={styles.closeButton}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        )}
      </NoSsr>
      <nav>
        <ul>{menusElements}</ul>
      </nav>
      <SidebarPane />
    </MenusContainer>
  );
}

// todo: consider taking common large type sets out
Menus.propTypes = {
  searchMode: PropTypes.bool,
  isOpen: PropTypes.bool,
  close: PropTypes.func
};
